/**
 * SimilarPlayersListView — the similar-players list body.
 *
 * One question, five answers: given a read's state, draw a spinner, an error
 * with a retry, the empty copy, the rows, or the rows behind a staleness
 * banner. It came out of `SimilarPlayersTabView`, where it sat inside a
 * `useMemo` in a 534-line file (SE-8019).
 *
 * The rows themselves are supplied as `renderPlayerList` rather than built
 * here: the list needs a memoized `renderItem`, a key extractor and a footer,
 * all of which belong to whoever owns the data.
 * @module features/player-detail/components/organisms/SimilarPlayersList/SimilarPlayersListView
 */

import { RefreshCwIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import PlayerDetailErrorState from "@/features/player-detail/components/molecules/PlayerDetailErrorState";
import type { SimilarPlayersListState } from "@/features/player-detail/types";

import type { SimilarPlayersPlayerResultFragment } from "@/generated/graphql";

/** Copy for the `failed` list state. */
const LIST_ERROR_TITLE = "Couldn't load similar players";
/**
 * Subtitle for the `failed` state.
 *
 * "That is not the same as there being none" is load-bearing: it is the whole
 * reason `failed` and `empty` are separate states rather than one "nothing to
 * show" screen.
 */
const LIST_ERROR_SUBTITLE =
  "We couldn't load this player's similar players. That is not the same as there being none — try again.";
/** Copy for the `stale` list state (results shown, refresh failed). */
const LIST_STALE_MESSAGE =
  "These results may be out of date — the last refresh failed.";

/** Props for {@link SimilarPlayersListView}. */
export interface SimilarPlayersListViewProps {
  /** The read's state, which decides which of the five bodies renders. */
  readonly listState: SimilarPlayersListState;
  /** Whether the dark theme is active. */
  readonly isDark: boolean;
  /** Whether a refresh is in flight — drives both retry affordances. */
  readonly isRefreshing: boolean;
  /** Retries the read, from the error state or the staleness banner. */
  readonly onRetry: () => void;
  /** Renders the rows themselves, supplied by whoever owns the data. */
  readonly renderPlayerList: (
    players: readonly SimilarPlayersPlayerResultFragment[]
  ) => React.ReactElement;
}

/**
 * Renders the list body for the given read state.
 *
 * Block-bodied on purpose: it is a five-way switch with an exhaustiveness
 * check, and a nested ternary chain would concentrate the branching without
 * making it clearer — the shape UpdateFeedList and LeagueGprSpectrum proved
 * costly earlier on this branch.
 * @param props - List props.
 * @returns The list body.
 */
const renderListBody = (
  props: SimilarPlayersListViewProps
): React.ReactElement => {
  switch (props.listState.kind) {
    case "loading":
      return (
        <Stack className="flex-1 items-center justify-center gap-4 py-12">
          <Spinner size="large" color={props.isDark ? "#60A5FA" : "#3B82F6"} />
          <Text variant="unstyled" className="text-content-muted">
            Loading similar players...
          </Text>
        </Stack>
      );
    case "failed":
      return (
        <PlayerDetailErrorState
          title={LIST_ERROR_TITLE}
          subtitle={LIST_ERROR_SUBTITLE}
          onRetry={props.onRetry}
          isRetrying={props.isRefreshing}
        />
      );
    case "empty":
      return (
        <Stack className="flex-1 items-center justify-center py-12">
          <Text variant="unstyled" className="text-center text-content-muted">
            No similar players found matching your filters.
          </Text>
        </Stack>
      );
    case "stale":
      return (
        <Stack className="min-h-0 flex-1">
          <Box accessibilityRole="alert">
            <Stack
              direction="horizontal"
              className="items-center justify-between gap-2 border-b border-outline-subtle bg-status-warning-surface px-4 py-2"
            >
              <Text
                variant="caption"
                className="shrink text-status-warning-strong"
              >
                {LIST_STALE_MESSAGE}
              </Text>
              <Button
                label="Retry"
                tone="secondary"
                variant="outline"
                size="sm"
                labelTone="secondary"
                labelVariant="caption"
                icon={RefreshCwIcon}
                iconTone="secondary"
                isLoading={props.isRefreshing}
                isDisabled={props.isRefreshing}
                onPress={props.onRetry}
              />
            </Stack>
          </Box>
          {props.renderPlayerList(props.listState.players)}
        </Stack>
      );
    case "ready":
      return props.renderPlayerList(props.listState.players);
    default: {
      const exhaustive: never = props.listState;
      return exhaustive;
    }
  }
};

/**
 * Renders the similar-players list body.
 * @param props - List props.
 * @returns The list body.
 */
const SimilarPlayersListView = (props: SimilarPlayersListViewProps) =>
  renderListBody(props);

SimilarPlayersListView.displayName = "SimilarPlayersListView";

export default memo(SimilarPlayersListView);
